import apiService from './api';
import { TableColumn } from '../types';
import { TableDataResponse } from '../types/api.types';
import { TABLE_CONFIG } from '../utils/constants';

interface UpdateCellRequest {
  tableId: string;
  rowId: string;
  field: string;
  value: any;
  oldValue?: any;
}

interface BatchUpdateRequest {
  tableId: string;
  updates: Array<{
    rowId: string;
    field: string;
    value: any;
  }>;
}

class TableService {
  // ========= 数据获取相关 =========

  /**
   * 获取表格数据 - 从 api.ts 迁移过来
   * @param tableId 表格ID
   * @returns 处理后的表格数据
   */
  async fetchTableData(tableId: string): Promise<TableDataResponse> {
    try {
      const response = await apiService.getTableData(tableId);
      return this._transformApiResponse(response.data);
    } catch (error) {
      console.error('获取表格数据失败:', error);
      throw error;
    }
  }

  // ========= 数据更新相关 =========

  /**
   * 更新单个单元格数据
   * @param request 更新请求参数
   * @returns 更新结果
   */
  async updateCellData(request: UpdateCellRequest): Promise<any> {
    const { tableId, rowId, field, value, oldValue } = request;
    
    try {
      // 构建更新数据
      const updateData = { [field]: value };
      
      console.log('更新单元格数据:', {
        tableId,
        rowId,
        field,
        value,
        oldValue
      });

      // 调用API更新数据
      const response = await apiService.updateTableItem(tableId, rowId, updateData);
      
      console.log('单元格数据更新成功:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('更新单元格数据失败:', error);
      
      // 根据错误类型提供更友好的错误信息
      if (error.response?.status === 403) {
        throw new Error('没有编辑权限');
      } else if (error.response?.status === 404) {
        throw new Error('数据不存在');
      } else if (error.response?.status === 422) {
        throw new Error('数据格式不正确');
      }
      
      throw error;
    }
  }

  /**
   * 批量更新表格数据
   * @param request 批量更新请求参数
   * @returns 更新结果
   */
  async batchUpdateData(request: BatchUpdateRequest): Promise<any> {
    const { tableId, updates } = request;
    
    try {
      // 构建批量更新数据
      const batchData = updates.map(update => ({
        id: update.rowId,
        data: { [update.field]: update.value }
      }));

      console.log('批量更新表格数据:', { tableId, batchData });

      const response = await apiService.batchUpdateTableItems(tableId, batchData);
      
      console.log('批量更新成功:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('批量更新失败:', error);
      throw error;
    }
  }

  /**
   * 验证单元格数据
   * @param field 字段名
   * @param value 新值
   * @param column 列配置
   * @returns 验证结果
   */
  validateCellData(field: string, value: any, column: TableColumn): { isValid: boolean; error?: string } {
    try {
      // 根据字段类型进行特定验证
      switch (column.cellType) {
        case 'input':
          // 文本输入验证
          if (typeof value !== 'string' && value !== null) {
            return { isValid: false, error: '请输入有效的文本' };
          }
          break;

        case 'singleSelect':
          // 单选验证
          if (value && column.options && !column.options.includes(value)) {
            return { isValid: false, error: '请选择有效的选项' };
          }
          break;

        case 'multiSelect':
          // 多选验证
          if (value && Array.isArray(value) && column.options) {
            const invalidOptions = value.filter(v => !column.options!.includes(v));
            if (invalidOptions.length > 0) {
              return { isValid: false, error: `无效的选项: ${invalidOptions.join(', ')}` };
            }
          }
          break;

        default:
          break;
      }

      // 特定字段的业务验证
      if (field === 'percentage') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          return { isValid: false, error: '百分比必须在0-100之间' };
        }
      }

      return { isValid: true };
      
    } catch (error) {
      console.error('验证数据时发生错误:', error);
      return { isValid: false, error: '数据验证失败' };
    }
  }

  generateDefaultColumns(): TableColumn[] {
    return [
      {
        field: 'phase', //phase
        title: '阶段',
        width: 120,
        cellType: 'singleSelect',
        options: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'],
      },
      {
        field: 'tradeName', //tradeName
        title: '商品名称',
        width: 200,
        cellType: 'link', //button
      },
      {
        field: 'inci', //inci
        title: '国际药品注册',
        width: 150,
        cellType: 'text',
      },
      {
        field: 'function', //function
        title: '功能',
        width: 180,
        cellType: 'multiSelect',
        options: ['润肤剂', '乳化剂', '护肤剂', '活性成分', '防腐剂', '闭塞', '保湿剂', 'PH调节剂', '粘度调节剂', '抗氧化剂', '美学增强剂', '螯合剂', '助乳化剂',"成膜剂","稳定器", "着色剂", "香味"],
      },
      {
        field: 'supplier', //supplier
        title: '供应商',
        width: 150,
        cellType: 'text',
      },
      {
        field: 'estimated', //estimated
        title: '估计的 %',
        width: 120,
        cellType: 'text',
      },
      {
        field: 'percentage', //percentage
        title: '百分比 (%)',
        width: 120,
        cellType: 'input',
      },
    ];
  }

  generateEmptyData(rowCount: number = 15): any[] {
    return Array.from({ length: rowCount }, (_, index) => ({
      id: `row_${index + 1}`,
      phase: [
        {
          label: 'A', value: 'A',
        },
      ],
      tradeName: '⚡︎ 检测商品名称',
      inci: '燕麦甘油',
      function: [
        {
          label: '润肤剂', value: '润肤剂',
        },
        {
          label: '抗氧化剂', value: '抗氧化剂',
        }
      ],
      supplier: 'MakingCosmetics公司',
      estimated: '1-2%',
      percentage: '',
    }));
  }

  validateTableData(data: any[]): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!Array.isArray(data)) {
      errors.push('数据格式不正确');
      return { isValid: false, errors };
    }

    const totalPercentage = data.reduce((sum, row) => {
      const percentage = parseFloat(row.percentage) || 0;
      return sum + percentage;
    }, 0);

    if (Math.abs(totalPercentage - 100) > 0.1) {
      errors.push(`总百分比应为100%，当前为${totalPercentage.toFixed(1)}%`);
    }

    data.forEach((row, index) => {
      if (!row.ingredient || row.ingredient.trim() === '') {
        errors.push(`第${index + 1}行：成分名称不能为空`);
      }

      const percentage = parseFloat(row.percentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        errors.push(`第${index + 1}行：百分比必须在0-100之间`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private _transformApiResponse(apiData: any): TableDataResponse {
    console.log("_transformApiResponse apiData:", apiData);
    
    // 转换API响应为标准格式
    // if (apiData.columns && apiData.records) {
    //   return {
    //     columns: this._transformColumns(apiData.columns),
    //     records: apiData.records,
    //     total: apiData.records.length,
    //   };
    // }

    // 如果API返回的是简单数组，使用默认列配置
    return {
      columns: this.generateDefaultColumns(),
      records: Array.isArray(apiData) ? apiData : [],
      total: Array.isArray(apiData) ? apiData.length : 0,
    };
  }

  private _transformColumns(apiColumns: any[]): TableColumn[] {
    return apiColumns.map(col => ({
      field: col.field || col.key,
      title: col.title || col.name,
      width: col.width || TABLE_CONFIG.DEFAULT_COLUMN_WIDTH,
      cellType: this._mapCellType(col.type),
      options: col.options,
    }));
  }

  private _mapCellType(apiType: string): TableColumn['cellType'] {
    const typeMap: Record<string, TableColumn['cellType']> = {
      'string': 'text',
      // 'number': 'number',
      'array': 'multiSelect',
      'select': 'singleSelect',
    };

    return typeMap[apiType] || 'text';
  }
}

export default new TableService();